import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { addDocs, Audience, AUDIENCES, currentUserLabel, DOC_TYPES, isInKnowledge, isSupportBotApprover, KnowledgeDoc, NewDocInput, OWNERS, PRODUCTS, resubmitDoc, useSupportBotDocs } from "./store";
import { inputCls, primaryBtn, secondaryBtn } from "./shared";

const ACCEPTED = ["pdf", "docx", "md", "txt", "html"];
const MAX_BYTES = 20 * 1024 * 1024;
const MAX_FILES = 10;

const formatSize = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
// "userGuide v3.pdf" and "userGuide.pdf" are the same document.
const docKey = (name: string) =>
  name
    .toLowerCase()
    .replace(/\.[a-z0-9]+$/, "")
    .replace(/[\s_-]*v\d+$/, "")
    .trim();

interface PickedFile {
  id: string;
  file: File;
  error?: string;
  duplicateOf?: string;
  asNewVersion?: boolean;
  details: Omit<NewDocInput, "name" | "size">;
  showPreview?: boolean;
}

// Client-side checks only for now (no API yet). Password-protected and
// scanned/image-only PDFs can only be caught once the backend extracts text.
const validate = (file: File, existing: KnowledgeDoc[]): Pick<PickedFile, "error" | "duplicateOf"> => {
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  if (!ACCEPTED.includes(ext)) return { error: "Unsupported format. Use PDF, DOCX, MD, TXT or HTML." };
  if (file.size > MAX_BYTES) return { error: `File is ${formatSize(file.size)}. Maximum is 20 MB.` };
  const dup = existing.find((d) => isInKnowledge(d) && docKey(d.name) === docKey(file.name));
  return dup ? { duplicateOf: dup.name } : {};
};

const isReady = (f: PickedFile) => !f.error && (!f.duplicateOf || f.asNewVersion);

const UploadModal: React.FC<{ onClose: () => void; revise?: KnowledgeDoc }> = ({ onClose, revise }) => {
  const docs = useSupportBotDocs();
  const approver = isSupportBotApprover();
  const me = currentUserLabel();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [files, setFiles] = useState<PickedFile[]>([]);
  const [note, setNote] = useState("");
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const defaultDetails = (): PickedFile["details"] => ({
    product: revise?.product || PRODUCTS[0],
    type: revise?.type || DOC_TYPES[0],
    visibleTo: revise?.visibleTo || ["Basic"],
    owner: me,
  });

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const room = (revise ? 1 : MAX_FILES) - files.length;
    const incoming = Array.from(list);
    if (incoming.length > room) toast.error(revise ? "Upload one revised file." : `Up to ${MAX_FILES} files per upload.`);
    const picked = incoming.slice(0, Math.max(0, room)).map((file, i) => {
      const v = validate(file, docs);
      // A revision of this exact document is expected to match it.
      return { id: `${Date.now()}_${i}`, file, ...v, duplicateOf: revise ? undefined : v.duplicateOf, details: defaultDetails() };
    });
    setFiles((prev) => [...prev, ...picked]);
  };

  const ready = files.filter(isReady);
  const patchFile = (id: string, patch: Partial<PickedFile>) => setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const patchDetails = (id: string, patch: Partial<PickedFile["details"]>) => setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, details: { ...f.details, ...patch } } : f)));
  const applyToAll = () => {
    const first = ready[0];
    if (!first) return;
    setFiles((prev) => prev.map((f) => (isReady(f) ? { ...f, details: { ...first.details } } : f)));
    toast.success("Applied the first file's details to all files");
  };

  const submit = () => {
    if (revise) {
      resubmitDoc(revise.id, me, ready[0].file.name);
      toast.success("Sent for approval", { description: `${ready[0].file.name} is back with the CRM Admin for review.` });
    } else {
      addDocs(
        ready.map((f) => ({ name: f.file.name, size: formatSize(f.file.size), ...f.details })),
        me,
        approver,
        note.trim() || undefined
      );
      toast.success(approver ? "Published — the bot will use this after testing" : "Sent for approval", {
        description: `${ready.length} file${ready.length > 1 ? "s" : ""} ${approver ? "added to the knowledge base" : "sent to a CRM Admin for review"}.`,
      });
    }
    onClose();
  };

  const stepTitle = step === 1 ? "Add files" : step === 2 ? "Details" : "Review & submit";
  const totalSteps = revise ? 1 : 3;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div className="w-full max-w-xl bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{revise ? "Upload revised file" : "Upload documents"}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {revise ? `Replaces ${revise.name}` : `Step ${step} of ${totalSteps} — ${stepTitle}`}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3">
          {step === 1 && (
            <>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  addFiles(e.dataTransfer.files);
                }}
                className={`rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
                  dragging ? "border-indigo-400 bg-indigo-50/60 dark:bg-indigo-500/10" : "border-gray-200 dark:border-gray-700"
                }`}
              >
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Drag & drop {revise ? "the revised file" : "files"} here</p>
                <p className="text-xs text-gray-400 my-2">or</p>
                <button type="button" onClick={() => inputRef.current?.click()} className={secondaryBtn}>
                  Browse files
                </button>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
                  PDF, DOCX, MD, TXT or HTML · up to 20 MB per file{revise ? "" : ` · up to ${MAX_FILES} files per upload`}
                </p>
                <input
                  ref={inputRef}
                  type="file"
                  multiple={!revise}
                  accept={ACCEPTED.map((e) => `.${e}`).join(",")}
                  className="hidden"
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
              </div>

              {files.map((f) => (
                <div key={f.id} className="flex items-start gap-3 rounded-xl border border-gray-100 dark:border-gray-800 px-4 py-3">
                  <FileText size={18} className="text-gray-400 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{f.file.name}</p>
                    <p className="text-xs text-gray-400">{formatSize(f.file.size)}</p>
                    {f.error && <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">{f.error}</p>}
                    {f.duplicateOf && !f.asNewVersion && (
                      <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">
                        Already in Knowledge as "{f.duplicateOf}".{" "}
                        <button type="button" onClick={() => patchFile(f.id, { asNewVersion: true })} className="font-semibold text-indigo-600 dark:text-indigo-400 underline">
                          Upload as new version
                        </button>
                      </p>
                    )}
                    {f.duplicateOf && f.asNewVersion && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">New version of "{f.duplicateOf}"</p>}
                  </div>
                  {isReady(f) ? (
                    <Check size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                  ) : (
                    <button type="button" onClick={() => setFiles((prev) => prev.filter((x) => x.id !== f.id))} className="text-gray-400 hover:text-gray-600 shrink-0">
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
            </>
          )}

          {step === 2 && (
            <>
              {ready.length > 1 && (
                <div className="flex justify-end">
                  <button type="button" onClick={applyToAll} className={secondaryBtn}>
                    Apply to all files
                  </button>
                </div>
              )}
              {ready.map((f) => (
                <div key={f.id} className="rounded-xl border border-gray-100 dark:border-gray-800 p-4">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">{f.file.name}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Product</span>
                      <select value={f.details.product} onChange={(e) => patchDetails(f.id, { product: e.target.value })} className={inputCls}>
                        {PRODUCTS.map((p) => (
                          <option key={p}>{p}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Type</span>
                      <select value={f.details.type} onChange={(e) => patchDetails(f.id, { type: e.target.value })} className={inputCls}>
                        {DOC_TYPES.map((t) => (
                          <option key={t}>{t}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <span className="block text-xs text-gray-500 dark:text-gray-400 mt-3 mb-1.5">Visible to</span>
                  <div className="flex flex-wrap gap-2">
                    {AUDIENCES.map((a) => {
                      const on = f.details.visibleTo.includes(a);
                      const toggle = () => {
                        const next: Audience[] = on ? f.details.visibleTo.filter((x) => x !== a) : [...f.details.visibleTo, a];
                        if (next.length) patchDetails(f.id, { visibleTo: AUDIENCES.filter((x) => next.includes(x)) });
                      };
                      return (
                        <button
                          key={a}
                          type="button"
                          onClick={toggle}
                          className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                            on ? "bg-indigo-50 text-indigo-700 border-indigo-300 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-500/40" : "bg-white text-gray-600 border-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-700"
                          }`}
                        >
                          {a}
                        </button>
                      );
                    })}
                  </div>
                  <label className="block mt-3">
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Owner</span>
                    <select value={f.details.owner} onChange={(e) => patchDetails(f.id, { owner: e.target.value })} className={inputCls}>
                      {Array.from(new Set([me, ...OWNERS])).map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </label>
                  <button type="button" onClick={() => patchFile(f.id, { showPreview: !f.showPreview })} className="mt-3 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline">
                    {f.showPreview ? "Hide extracted text preview" : "Show extracted text preview"}
                  </button>
                  {f.showPreview && (
                    <p className="mt-2 rounded-lg bg-gray-50 dark:bg-gray-800 px-3 py-2 text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                      Section 1 — Overview. This document explains how customers reset their CDSL CAS password and reconnect their broker account…
                    </p>
                  )}
                </div>
              ))}
            </>
          )}

          {step === 3 && (
            <>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Ready to {approver ? "publish" : "submit"} ({ready.length} file{ready.length > 1 ? "s" : ""})
              </p>
              {ready.map((f) => (
                <div key={f.id} className="rounded-xl border border-gray-100 dark:border-gray-800 px-4 py-3">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{f.file.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {f.details.product} · {f.details.type} · Visible to {f.details.visibleTo.join(", ")}
                  </p>
                </div>
              ))}
              {!approver && (
                <>
                  <label className="block pt-2">
                    <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1.5">Note for approver (optional)</span>
                    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="What changed and why…" className={`${inputCls} resize-y`} />
                  </label>
                  <p className="text-xs text-gray-400 dark:text-gray-500">A CRM Admin will be notified for review.</p>
                </>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 dark:border-gray-800">
          <button type="button" onClick={step === 1 ? onClose : () => setStep((s) => (s - 1) as 1 | 2)} className={secondaryBtn}>
            {step === 1 ? "Cancel" : "Back"}
          </button>
          {revise ? (
            <button type="button" disabled={ready.length === 0} onClick={submit} className={primaryBtn}>
              Submit for approval
            </button>
          ) : step < 3 ? (
            <button type="button" disabled={ready.length === 0} onClick={() => setStep((s) => (s + 1) as 2 | 3)} className={primaryBtn}>
              Continue
            </button>
          ) : (
            <button type="button" onClick={submit} className={primaryBtn}>
              {approver ? "Publish to knowledge base" : "Submit for approval"}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UploadModal;
